import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, MapPin } from 'lucide-react';
import shrileela_logo from '../../assets/logos/shrileela-logo.png';
import miransh_logo from '../../assets/logos/shrileela-logo.png';

const properties = [
  {
    id: 'shrileela',
    name: 'Shrileela Beach Resort',
    tagline: 'Where the ocean meets luxury',
    location: 'Konkan, Maharashtra',
    highlights: ['Beachfront Rooms', 'Authentic Dining', 'Water Sports'],
    route: '/shrileela',
    logo: shrileela_logo,
    accentColor: '#2D4A2D',
    bgPattern: 'linear-gradient(145deg, #2D4A2D 0%, #1a3320 100%)',
    filter:'brightness(0) invert(1)',
  },
  {
    id: 'miransh',
    name: 'Miransh',
    tagline: 'A retreat crafted for the senses',
    location: 'Konkan, Maharashtra',
    highlights: ['Luxury Suites', 'Spa & Wellness', 'Private Retreats'],
    route: '/miransh',
    logo: miransh_logo,
    accentColor: '#1a3a5c',
    bgPattern: 'linear-gradient(145deg, #1a3a5c 0%, #0d2440 100%)',
  },
];

const OtherPropertiesSection = () => {
  const navigate = useNavigate();

  return (
    <section style={styles.section}>
      {/* Section Header */}
      <div style={styles.header}>
        <p style={styles.eyebrow}>Explore More</p>
        <h2 style={styles.title}>Our Other Properties</h2>
        <p style={styles.subtitle}>
          Discover our curated collection of coastal escapes along the Konkan coast
        </p>
      </div>

      {/* Cards Grid */}
      <div style={styles.grid}>
        {properties.map((property) => (
          <div
            key={property.id}
            style={styles.card}
            onClick={() => navigate(property.route)}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-6px)';
              e.currentTarget.style.boxShadow = '0 20px 50px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 20px rgba(0,0,0,0.08)';
            }}
          >
            {/* Card visual area */}
            <div
              style={{
                ...styles.cardVisual,
                background: property.bgPattern,
              }}
            >
              <img
                src={property.logo}
                alt={property.name}
                style={styles.logo}
              />
            </div>

            {/* Card body */}
            <div style={styles.cardBody}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '6px' }}>
                <MapPin size={13} color="#C4933F" />
                <span style={styles.location}>{property.location}</span>
              </div>

              <h3 style={styles.cardTitle}>{property.name}</h3>
              <p style={styles.cardTagline}>{property.tagline}</p>

              {/* Highlights */}
              <div style={styles.highlights}>
                {property.highlights.map((tag) => (
                  <span key={tag} style={styles.tag}>{tag}</span>
                ))}
              </div>

              {/* CTA */}
              <button
                style={{
                  ...styles.cta,
                  color: property.accentColor,
                  borderColor: property.accentColor,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = property.accentColor;
                  e.currentTarget.style.color = '#fff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = property.accentColor;
                }}
              >
                Explore Property
                <ArrowRight size={15} style={{ marginLeft: '6px' }} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

const styles = {
  section: {
    padding: '80px 5%',
    background: '#FDFBF7',
  },
  header: {
    textAlign: 'center',
    marginBottom: '50px',
  },
  eyebrow: {
    fontSize: '13px',
    letterSpacing: '3px',
    textTransform: 'uppercase',
    color: '#C4933F',
    marginBottom: '10px',
    fontWeight: '500',
  },
  title: {
    fontSize: 'clamp(28px, 4vw, 40px)',
    fontWeight: '600',
    color: '#1a1a1a',
    margin: '0 0 14px',
    fontFamily: "'Georgia', serif",
  },
  subtitle: {
    fontSize: '16px',
    color: '#666',
    maxWidth: '480px',
    margin: '0 auto',
    lineHeight: '1.6',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '28px',
    maxWidth: '900px',
    margin: '0 auto',
  },
  card: {
    background: '#fff',
    borderRadius: '16px',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
  cardVisual: {
    height: '220px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  logo: {
    maxWidth: '60%',
    maxHeight: '60%',
    objectFit: 'contain',
    filter: 'brightness(0) invert(1)',
  },
  cardBody: {
    padding: '22px 24px 24px',
  },
  location: {
    fontSize: '12px',
    color: '#C4933F',
    fontWeight: '500',
    letterSpacing: '0.5px',
  },
  cardTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#1a1a1a',
    margin: '0 0 6px',
    fontFamily: "'Georgia', serif",
  },
  cardTagline: {
    fontSize: '14px',
    color: '#888',
    margin: '0 0 16px',
    lineHeight: '1.5',
  },
  highlights: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '20px',
  },
  tag: {
    fontSize: '11px',
    padding: '4px 10px',
    borderRadius: '20px',
    background: '#f5f0e8',
    color: '#7a6040',
    fontWeight: '500',
    letterSpacing: '0.3px',
  },
  cta: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '13px',
    fontWeight: '600',
    padding: '9px 18px',
    borderRadius: '6px',
    border: '1.5px solid',
    background: 'transparent',
    cursor: 'pointer',
    letterSpacing: '0.3px',
    transition: 'background 0.2s ease, color 0.2s ease',
  },
};

export default OtherPropertiesSection;
